import React, { useState } from 'react';
import { useHoldersSummary, useHoldersTop, useHoldersRisk } from '../../hooks/useApi/holders';
import DataTable from './shared/DataTable';
import KPICard from './shared/KPICard';
import { BarChart } from './shared/Chart';
import ErrorBoundary from './shared/ErrorBoundary';

const HoldersTab = () => {
  const [riskThreshold, setRiskThreshold] = useState(5);
  
  const { data: holdersSummary, isLoading: summaryLoading, isError: summaryError } = useHoldersSummary();
  const { data: topHolders, isLoading: topLoading, isError: topError } = useHoldersTop(50);
  const { data: riskData, isLoading: riskLoading, isError: riskError } = useHoldersRisk(riskThreshold);

  const formatNumber = (num) => {
    if (!num) return '0';
    return num.toLocaleString();
  };

  const formatAddress = (address) => {
    if (!address) return 'Unknown';
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const formatRelativeTime = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const now = new Date();
    const time = new Date(timestamp);
    const diffMs = now - time;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays > 365) return `${Math.floor(diffDays / 365)}y ago`;
    if (diffDays > 30) return `${Math.floor(diffDays / 30)}mo ago`;
    if (diffDays > 0) return `${diffDays}d ago`;
    return 'Today';
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const holdersColumns = [
    {
      key: 'name',
      header: 'Name',
      render: (value, row) => (
        <div className="holder-name">
          <span className="name">{value || row.tzdomain || 'Unknown'}</span>
          <span className="address" title={row.address}>
            {formatAddress(row.address)}
          </span>
        </div>
      )
    },
    {
      key: 'quantity',
      header: 'Quantity',
      render: (value) => formatNumber(value)
    },
    {
      key: 'oldest_hold',
      header: 'Oldest Hold',
      render: (value) => formatRelativeTime(value)
    }
  ];


  return (
    <ErrorBoundary>
      <div className="holders-tab">
        {/* Header */}
        <div className="holders-header">
          <h2>Holders Analysis</h2>
          <p>Distribution and concentration analysis of NFT holders</p>
        </div>

        {/* Main Content Grid */}
        <div className="holders-content-grid">
          {/* Left Column - Summary & Risk */}
          <div className="holders-summary-section">
            {/* Key Metrics */}
            <div className="summary-metrics">
              <h3>Key Metrics</h3>
              <div className="metrics-grid">
                <div className="metric-card featured">
                  <div className="metric-header">
                    <span className="metric-label">Unique Holders</span>
                  </div>
                  <span className="metric-value">{holdersSummary?.unique ? formatNumber(holdersSummary.unique) : 'Loading...'}</span>
                </div>
                <div className="metric-card featured">
                  <div className="metric-header">
                    <span className="metric-label">Total Supply</span>
                  </div>
                  <span className="metric-value">{holdersSummary?.totalSupply ? formatNumber(holdersSummary.totalSupply) : 'Loading...'}</span>
                </div>
                <div className="metric-card">
                  <div className="metric-header">
                    <span className="metric-label">Top 10 Share</span>
                  </div>
                  <span className="metric-value">{holdersSummary?.top10Share ? `${holdersSummary.top10Share.toFixed(1)}%` : 'Loading...'}</span>
                </div>
                <div className="metric-card">
                  <div className="metric-header">
                    <span className="metric-label">Top 20 Share</span>
                  </div>
                  <span className="metric-value">{holdersSummary?.top20Share ? `${holdersSummary.top20Share.toFixed(1)}%` : 'Loading...'}</span>
                </div>
              </div>
            </div>

            {/* Concentration Risk */}
            <div className="risk-analysis">
              <div className="risk-header">
                <h3>Concentration Risk</h3>
                <div className="risk-controls">
                  <label htmlFor="risk-threshold">Threshold:</label>
                  <select 
                    id="risk-threshold"
                    value={riskThreshold} 
                    onChange={(e) => setRiskThreshold(Number(e.target.value))}
                  >
                    <option value={1}>1+ NFTs</option>
                    <option value={2}>2+ NFTs</option>
                    <option value={3}>3+ NFTs</option>
                    <option value={5}>5+ NFTs</option>
                    <option value={10}>10+ NFTs</option>
                  </select>
                </div>
              </div>
              
              <div className="risk-metrics">
                <div className="risk-card">
                  <div className="risk-header">
                    <span className="risk-label">Wallets &gt; {riskThreshold} NFTs</span>
                  </div>
                  <span className="risk-value">{riskData?.walletsOverN ? formatNumber(riskData.walletsOverN) : 'Loading...'}</span>
                </div>
              </div>

              {riskData?.distribution && (
                <div className="risk-distribution">
                  <h4>Holdings Distribution</h4>
                  <div className="distribution-chart">
                    <BarChart 
                      data={riskData.distribution} 
                      loading={riskLoading}
                      className="risk-chart"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Top Holders Table */}
          <div className="top-holders-section">
            <div className="holders-table-header">
              <div className="table-title-section">
                <h3>Top Holders</h3>
                <span className="table-subtitle">Ranked by quantity held</span>
              </div>
              <div className="table-stats">
                <div className="table-stat">
                  <span className="stat-label">Total NFTs</span>
                  <span className="stat-value">
                    {topHolders ? formatNumber(topHolders.reduce((sum, holder) => sum + (holder.quantity || 0), 0)) : '0'}
                  </span>
                </div>
              </div>
            </div>
            <div className="holders-table-container">
              <DataTable
                columns={holdersColumns}
                data={topHolders || []}
                loading={topLoading}
                emptyMessage="No holder data available"
                className="holders-table"
              />
            </div>
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default HoldersTab;
